import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { Role } from '@/types';
import { ArrowLeft, Check, Shield, Info, Trash, Key } from 'lucide-react';
import { useRole, useRoles } from '@/store/role';
import { usePermissions } from '@/store/permission';

import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { InputField } from '@/components/ui/InputField';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';

export const ManageRolePage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();
    
    const getRole = useRoles((state) => state.get);
    const updateRole = useRoles((state) => state.update);
    const deleteRole = useRoles((state) => state.delete);
    const roleStore = useRole(id);
    
    const { permissions, list: listPermissions } = usePermissions();

    const [role, setRole] = useState<Role | null>(null);
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Delete state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    const fetch = async () => {
        if (!id) return;
        try {
            const r = await getRole(id);
            const perms = await roleStore.permissions.list();
            setRole(r);
            setSelectedPermissions(perms);
            setBreadcrumbs([{ name: 'Roles', to: '/m/roles' }, { name: r.name || 'Edit Role' }]);
        } catch {
            toast.error('Failed to load role configuration');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        listPermissions().catch(console.error);
        fetch();
    }, [id]);

    // Parse resource kind from the role ID
    const inferredResourceKindId = role?.id && role.id.includes('::')
        ? role.id.split('::').slice(0, -1).join('::')
        : '';

    // Filter permissions for the role's resource kind
    const availablePermissions = permissions.filter(
        (p) => p.resource_kind_id === inferredResourceKindId
    );

    const togglePermission = async (permId: string) => {
        const isGranted = selectedPermissions.includes(permId);
        try {
            if (isGranted) {
                await roleStore.permissions.delete(permId);
                setSelectedPermissions((prev) => prev.filter((x) => x !== permId));
                toast.success('Permission revoked');
            } else {
                await roleStore.permissions.create(permId);
                setSelectedPermissions((prev) => [...prev, permId]);
                toast.success('Permission granted');
            }
        } catch {
            toast.error('Failed to update permission association');
        }
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!role || !id) return;
        setIsSaving(true);
        try {
            await updateRole(id, {
                name: role.name,
                description: role.description || '',
            });
            toast.success('Role updated');
        } catch {
            toast.error('Failed to update role');
        } finally {
            setIsSaving(false);
        }
    };

    const confirmDeletion = async () => {
        if (confirmText !== role?.name) return;
        setIsDeleting(true);
        try {
            await deleteRole(id!);
            toast.success('Role deleted');
            navigate('/m/roles');
        } catch {
            toast.error('Failed to delete role');
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    if (loading || !role) return <div className="h-full flex items-center justify-center"><Loader /></div>;

    return (
        <div className="p-4 lg:p-8 max-w-4xl mx-auto w-full animate-in fade-in duration-500">
            <PageHeader
                title={role.name}
                subtitle={`Managing role: ${inferredResourceKindId || 'None'}`}
                actions={
                    <Button variant="ghost" onClick={() => navigate('/m/roles')} className="gap-2">
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </Button>
                }
            />

            <div className="mt-8 animate-in slide-in-from-bottom-4 duration-500">
                <Card
                    title="Role Configuration"
                    subtitle="Configure role details and manage assigned permissions."
                    className="glass"
                >
                    <form onSubmit={submit} className="space-y-8">
                        <div className="space-y-6">
                            {/* Read-only Role ID */}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Role ID / Key
                                </label>
                                <div className="flex rounded-lg border border-slate-100 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-950/20 overflow-hidden">
                                    <div className="relative flex-1 flex items-center">
                                        <div className="absolute left-3 text-slate-400">
                                            <Key className="w-4 h-4" />
                                        </div>
                                        <input
                                            type="text"
                                            className="w-full pl-10 pr-4 py-2 bg-transparent text-sm outline-none text-slate-400 dark:text-slate-500 font-mono cursor-not-allowed"
                                            value={role.id}
                                            disabled
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    Role ID is immutable after creation. To change the identifier, you must delete and recreate the role.
                                </p>
                            </div>

                            {/* Editable Role Name */}
                            <InputField
                                label="Role Name"
                                value={role.name}
                                onChange={(e) => setRole({ ...role, name: e.target.value })}
                                required
                                icon={<Shield className="w-4 h-4" />}
                            />

                            {/* Editable Description */}
                            <InputField
                                label="Brief Description"
                                value={role.description || ''}
                                onChange={(e) => setRole({ ...role, description: e.target.value })}
                                icon={<Info className="w-4 h-4" />}
                            />

                            {/* Read-only Resource Kind */}
                            {inferredResourceKindId && (
                                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                                        Resource Kind
                                    </label>
                                    <span className="text-sm font-mono font-bold text-slate-900 dark:text-white bg-white dark:bg-slate-800 px-3 py-1 rounded-lg border border-slate-200 dark:border-slate-700 inline-block mt-1">
                                        {inferredResourceKindId}
                                    </span>
                                    <p className="text-[10px] text-slate-500 mt-2">
                                        Resource Kind is inferred from the Role ID scope and is immutable.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Permissions checklist */}
                        {inferredResourceKindId && (
                            <div className="pt-6 border-t border-slate-100 dark:border-slate-800 space-y-4">
                                <div>
                                    <h3 className="text-sm font-bold text-slate-900 dark:text-white">Role Permissions</h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        Grant or revoke permissions scoped under the <code className="font-mono">{inferredResourceKindId}</code> Resource Kind.
                                    </p>
                                </div>

                                {availablePermissions.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto p-1 border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-50/50 dark:bg-slate-950/20">
                                        {availablePermissions.map((perm) => (
                                            <div
                                                key={perm.id}
                                                onClick={() => togglePermission(perm.id)}
                                                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                                    selectedPermissions.includes(perm.id)
                                                        ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-500/10'
                                                        : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-white dark:bg-slate-900'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedPermissions.includes(perm.id)}
                                                    onChange={() => {}}
                                                    className="w-4 h-4 rounded text-orange-500 focus:ring-orange-500 border-slate-300 dark:border-slate-700 mt-0.5"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <span className="text-sm font-bold text-slate-900 dark:text-white block truncate">
                                                        {perm.name}
                                                    </span>
                                                    <span className="text-[10px] text-slate-500 dark:text-slate-300 font-mono block truncate mt-0.5">
                                                        {perm.id.split('::').pop()}
                                                    </span>
                                                    {perm.description && (
                                                        <span className="text-[10px] text-slate-600 dark:text-slate-400 block truncate mt-1">
                                                            {perm.description}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-6 text-center border border-dashed border-slate-200 dark:border-slate-800 rounded-lg text-slate-500 text-sm flex flex-col items-center gap-2">
                                        <Info className="w-5 h-5 text-slate-400" />
                                        <span>No permissions defined for Resource Kind <code className="font-mono">{inferredResourceKindId}</code> yet.</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="pt-6 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                            <Button type="submit" isLoading={isSaving} className="min-w-40 gap-2">
                                <Check className="w-4 h-4" />
                                Save Changes
                            </Button>
                        </div>
                    </form>
                </Card>

                <Card className="mt-6 border-red-100 dark:border-red-900/20 bg-red-50/10 glass" title="Danger Zone">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-sm font-bold text-slate-900 dark:text-white">Delete Role</h4>
                            <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">Permanently remove this role from the system. This action is irreversible.</p>
                        </div>
                        <Button variant="danger" size="sm" onClick={() => { setConfirmText(''); setShowDeleteConfirm(true); }} className="gap-2">
                            <Trash className="w-4 h-4" />
                            Delete
                        </Button>
                    </div>
                </Card>
            </div>

            {/* ─── Delete confirm modal ─── */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-slate-900/50 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
                    <Card className="w-full max-w-md shadow-2xl border-red-200 dark:border-red-900/30">
                        <div className="flex flex-col items-center text-center py-4">
                            <div className="w-12 h-12 bg-red-100 dark:bg-red-500/10 text-red-600 rounded-full flex items-center justify-center mb-4">
                                <Trash className="w-6 h-6" />
                            </div>
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Delete Role</h2>
                            <p className="text-sm text-slate-500 mt-2 px-4">
                                This will permanently remove <span className="font-bold text-slate-900 dark:text-white">{role.name}</span>.
                                All associated user and group assignments will be revoked immediately.
                            </p>

                            <div className="w-full mt-6 text-left space-y-3">
                                <InputField
                                    label={`Type "${role.name}" to confirm`}
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                    placeholder={role.name}
                                />
                            </div>

                            <div className="w-full flex gap-3 mt-8">
                                <Button variant="ghost" className="flex-1" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
                                <Button
                                    variant="danger"
                                    className="flex-1"
                                    disabled={confirmText !== role.name || isDeleting}
                                    isLoading={isDeleting}
                                    onClick={confirmDeletion}
                                >
                                    Confirm Delete
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};
